import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import NumberFormat from 'react-number-format';
import { Form, Col, Card, Alert } from 'react-bootstrap';
import Modal from '../../components/Modal';
import Button from '../../components/Button';
import { CadernoLPMALocalLista, CadernoLPMATransporte } from '../../models/modelsCadernoLPMA';
import { AMISearchResult } from '../../models/modelsAuthorizationsCIBIO';
import { STRINGS_CADERNOLPMA } from '../../util/cadernoLPMA/string';
import {
	converterPesoKgParaNumero,
	converterPesoKgParaNumeroTransporte,
	formatPesoKgValorInicial,
} from '../../util/cadernoLPMA/pesoKgMask';
import api from '../../services/api';
import AssinaturaComNomesAdicionais, {
	parseNomesEnvolvidosJson,
	stringifyNomesEnvolvidos,
} from './components/AssinaturaComNomesAdicionais';
import { FaTrashAlt } from 'react-icons/fa';

type LocalOption = { label: string; value: string };

const formatarLocalParaLabel = (local: CadernoLPMALocalLista): string => {
	const nomeCompleto = local.nomeCompleto?.trim();
	const endereco = local.endereco?.trim();
	const cidade = local.cidade?.trim();
	const uf = local.uf?.trim();
	const cidadeUf = [cidade, uf].filter(Boolean).join('/');
	const principal = [nomeCompleto, endereco].filter(Boolean).join(' - ');
	const partes = [principal, cidadeUf].filter(Boolean);
	return partes.join(' - ');
};

type TransporteFormValues = CadernoLPMATransporte & {
	aac?: any;
	outrasDataRecebimento?: Date | string | null;
	outrasDataPlantio?: Date | string | null;
	outrasDataSaldo?: Date | string | null;
	ogmAmostrasSaldo?: any;
	ogmPesoSaldo?: any;
	outrasAmostrasSaldo?: any;
	outrasPesoSaldo?: any;
	sobrasSementesSemeadora?: any;
	quantidadeSobrasSemeadoraKg?: any;
};

interface TransporteModalProps {
	show: boolean;
	onHide: () => void;
	onSubmit: (info: CadernoLPMATransporte) => void;
	initialData?: CadernoLPMATransporte;
	processoCTNBioReferencia?: string;
}

const parseDateValue = (valor: any): Date | null => {
	if (!valor && valor !== 0) {
		return null;
	}

	if (valor instanceof Date) {
		return Number.isNaN(valor.getTime()) ? null : valor;
	}

	if (typeof valor === 'string') {
		const texto = valor.trim();
		if (!texto) {
			return null;
		}

		const data = new Date(texto);
		return Number.isNaN(data.getTime()) ? null : data;
	}

	if (typeof valor === 'number') {
		const data = new Date(valor);
		return Number.isNaN(data.getTime()) ? null : data;
	}

	return null;
};

const defaultValues: TransporteFormValues = {
	id: 0,
	cadernoLPMAId: 0,
	multiplicadaCampo: false,
	processoCIBIO: '',
	localOrigemCampo: '',
	multiplicadaCage: false,
	aac: '',
	localOrigemCage: '',
	importada: false,
	processoMAPA: '',
	processoImportacaoIP: '',
	dataRecebimento: null,
	outrasDataRecebimento: null,
	ogmAmostrasRecebimento: '',
	ogmPesoRecebimento: '',
	outrasDescricao: '',
	outrasAmostrasRecebimento: '',
	outrasPesoRecebimento: '',
	dataPlantio: null,
	outrasDataPlantio: null,
	ogmAmostrasPlantio: '',
	ogmPesoPlantio: '',
	outrasAmostrasPlantio: '',
	outrasPesoPlantio: '',
	dataSaldo: null,
	outrasDataSaldo: null,
	ogmAmostrasSaldo: '',
	ogmPesoSaldo: '',
	outrasAmostrasSaldo: '',
	outrasPesoSaldo: '',
	ami: '',
	cartaTransferencia: '',
	responsavel: '',
	dataResponsavel: null,
	destruicaoDescarte: false,
	armazenamento: false,
	devolucao: false,
	observacao: '',
	sobrasSementesSemeadora: false,
	quantidadeSobrasSemeadoraKg: '',
	origemCoincideLPMA: false,
	observacaoOrigemCoincideLPMA: '',
	transporteAutorizado: false,
	observacaoTransporteAutorizado: '',
	embalagens: false,
	observacaoEmbalagens: '',
	duplaContencao: false,
	observacaoDuplaContencao: '',
	concordanciaAMI: false,
	observacaoConcordanciaAMI: '',
	localPossuiCQB: false,
	observacaoLocalPossuiCQB: '',
	quantidadeRegistrada: false,
	observacaoQuantidadeRegistrada: '',
};

const converterValoresIniciais = (
	dados?: CadernoLPMATransporte | null,
): TransporteFormValues => {
	if (!dados) {
		return { ...defaultValues };
	}

	return {
		...defaultValues,
		...dados,
		dataRecebimento: parseDateValue(dados.dataRecebimento),
		outrasDataRecebimento: parseDateValue(dados.outrasDataRecebimento),
		dataPlantio: parseDateValue(dados.dataPlantio),
		outrasDataPlantio: parseDateValue(dados.outrasDataPlantio),
		dataSaldo: parseDateValue(dados.dataSaldo),
		outrasDataSaldo: parseDateValue(dados.outrasDataSaldo),
		dataResponsavel: parseDateValue(dados.dataResponsavel),
		quantidadeSobrasSemeadoraKg: formatPesoKgValorInicial(dados.quantidadeSobrasSemeadoraKg),
		ogmPesoRecebimento: formatPesoKgValorInicial(dados.ogmPesoRecebimento),
		outrasPesoRecebimento: formatPesoKgValorInicial(dados.outrasPesoRecebimento),
		ogmPesoPlantio: formatPesoKgValorInicial(dados.ogmPesoPlantio),
		outrasPesoPlantio: formatPesoKgValorInicial(dados.outrasPesoPlantio),
		ogmPesoSaldo: formatPesoKgValorInicial(dados.ogmPesoSaldo),
		outrasPesoSaldo: formatPesoKgValorInicial(dados.outrasPesoSaldo),
	};
};

const amostrasCampos: (keyof TransporteFormValues)[] = [
	'ogmAmostrasRecebimento',
	'outrasAmostrasRecebimento',
	'ogmAmostrasPlantio',
	'outrasAmostrasPlantio',
	'ogmAmostrasSaldo',
	'outrasAmostrasSaldo',
];

const pesoCamposTabela: (keyof TransporteFormValues)[] = [
	'ogmPesoRecebimento',
	'outrasPesoRecebimento',
	'ogmPesoPlantio',
	'outrasPesoPlantio',
	'ogmPesoSaldo',
	'outrasPesoSaldo',
];

const numericCampos: (keyof TransporteFormValues)[] = [
	...amostrasCampos,
	...pesoCamposTabela,
];

const chavesPesoLinhaExtra = [
	'outrasPesoRecebimento',
	'outrasPesoPlantio',
	'outrasPesoSaldo',
] as const;

const pesoStringTemVirgula = (valor: unknown): boolean =>
	typeof valor === 'string' && valor.trim().length > 0 && valor.includes(',');

const camposData: (keyof TransporteFormValues)[] = [
	'dataRecebimento',
	'outrasDataRecebimento',
	'dataPlantio',
	'outrasDataPlantio',
	'dataSaldo',
	'outrasDataSaldo',
	'dataResponsavel',
];

const converterNumero = (valor: any): number | null => {
	if (valor === '' || valor === undefined || valor === null) {
		return null;
	}

	if (typeof valor === 'string') {
		const normalizado = valor.replace(/\./g, '').replace(',', '.').trim();
		if (!normalizado) {
			return null;
		}

		const numeroNormalizado = Number(normalizado);
		return Number.isFinite(numeroNormalizado) ? numeroNormalizado : null;
	}

	const numero = Number(valor);
	return Number.isFinite(numero) ? numero : null;
};

const temValor = (valor: any): boolean => {
	if (valor === null || valor === undefined) return false;
	if (typeof valor === 'string') return valor.trim().length > 0;
	return true;
};

type OutraSementeExtraLinha = {
	clientKey: string;
	texto: string;
	outrasDataRecebimento: Date | null;
	outrasDataPlantio: Date | null;
	outrasDataSaldo: Date | null;
	outrasAmostrasRecebimento: string;
	outrasPesoRecebimento: string;
	outrasAmostrasPlantio: string;
	outrasPesoPlantio: string;
	outrasAmostrasSaldo: string;
	outrasPesoSaldo: string;
};

const novaDescricaoOutraExtra = (): OutraSementeExtraLinha => ({
	clientKey: `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
	texto: '',
	outrasDataRecebimento: null,
	outrasDataPlantio: null,
	outrasDataSaldo: null,
	outrasAmostrasRecebimento: '',
	outrasPesoRecebimento: '',
	outrasAmostrasPlantio: '',
	outrasPesoPlantio: '',
	outrasAmostrasSaldo: '',
	outrasPesoSaldo: '',
});

const strCampoJson = (v: unknown): string => {
	if (v === null || v === undefined) return '';
	return String(v);
};

const jsonPick = (o: Record<string, unknown>, key: string): unknown => {
	const k = Object.keys(o).find((kk) => kk.toLowerCase() === key.toLowerCase());
	return k !== undefined ? o[k] : undefined;
};

const strCampoJsonNum = (v: unknown): string => {
	if (v === null || v === undefined) return '';
	if (typeof v === 'number' && Number.isFinite(v)) return String(v);
	return strCampoJson(v);
};

const temAlgumDadoLinhaExtra = (row: OutraSementeExtraLinha): boolean => {
	if (temValor(row.texto)) return true;
	if (parseDateValue(row.outrasDataRecebimento)) return true;
	if (parseDateValue(row.outrasDataPlantio)) return true;
	if (parseDateValue(row.outrasDataSaldo)) return true;
	return [
		row.outrasAmostrasRecebimento,
		row.outrasPesoRecebimento,
		row.outrasAmostrasPlantio,
		row.outrasPesoPlantio,
		row.outrasAmostrasSaldo,
		row.outrasPesoSaldo,
	].some(temValor);
};

const parseUmOutraExtraItem = (item: unknown, i: number): OutraSementeExtraLinha | null => {
	if (typeof item === 'string') {
		const d = item.trim();
		if (!d) return null;
		return { ...novaDescricaoOutraExtra(), texto: d, clientKey: `loaded-${i}-${Date.now()}` };
	}
	const o = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
	const texto = strCampoJson(jsonPick(o, 'descricao')).trim();
	const row: OutraSementeExtraLinha = {
		clientKey: `loaded-${i}-${Date.now()}`,
		texto,
		outrasDataRecebimento: parseDateValue(jsonPick(o, 'outrasDataRecebimento')),
		outrasDataPlantio: parseDateValue(jsonPick(o, 'outrasDataPlantio')),
		outrasDataSaldo: parseDateValue(jsonPick(o, 'outrasDataSaldo')),
		outrasAmostrasRecebimento: strCampoJsonNum(jsonPick(o, 'outrasAmostrasRecebimento')),
		outrasPesoRecebimento: formatPesoKgValorInicial(jsonPick(o, 'outrasPesoRecebimento')),
		outrasAmostrasPlantio: strCampoJsonNum(jsonPick(o, 'outrasAmostrasPlantio')),
		outrasPesoPlantio: formatPesoKgValorInicial(jsonPick(o, 'outrasPesoPlantio')),
		outrasAmostrasSaldo: strCampoJsonNum(jsonPick(o, 'outrasAmostrasSaldo')),
		outrasPesoSaldo: formatPesoKgValorInicial(jsonPick(o, 'outrasPesoSaldo')),
	};
	if (!temAlgumDadoLinhaExtra(row)) return null;
	return row;
};

const parseDescricoesOutrasExtrasJson = (json?: string | null): OutraSementeExtraLinha[] => {
	const texto = (json ?? '').trim();
	if (!texto) return [];
	try {
		const arr = JSON.parse(texto) as unknown[];
		if (!Array.isArray(arr)) return [];
		return arr.map((item, i) => parseUmOutraExtraItem(item, i)).filter((x): x is OutraSementeExtraLinha => x != null);
	} catch {
		return [];
	}
};

type AmiAdicionalLinha = {
	clientKey: string;
	ami: string;
};

const novaAmiAdicional = (): AmiAdicionalLinha => ({
	clientKey: `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
	ami: '',
});

const parseAmisAdicionaisJson = (json?: string | null): AmiAdicionalLinha[] => {
	const texto = (json ?? '').trim();
	if (!texto) return [];
	try {
		const arr = JSON.parse(texto) as unknown[];
		if (!Array.isArray(arr)) return [];
		return arr
			.map((item, i) => {
				let amiVal = '';
				if (typeof item === 'string') {
					amiVal = item;
				} else if (item && typeof item === 'object') {
					const o = item as Record<string, unknown>;
					amiVal = typeof o['ami'] === 'string' ? o['ami'] : '';
				}
				if (!amiVal.trim()) return null;
				return { clientKey: `loaded-ami-${i}-${Date.now()}`, ami: amiVal };
			})
			.filter((x): x is AmiAdicionalLinha => x != null);
	} catch {
		return [];
	}
};

const serializarAmisAdicionais = (itens: AmiAdicionalLinha[]): string | null => {
	const validos = itens.filter((i) => i.ami.trim());
	if (validos.length === 0) return null;
	return JSON.stringify(validos.map((i) => ({ ami: i.ami.trim() })));
};

const urlObterAmiPorNumeroAutorizacaoCibio = (texto: string): string =>
	`api/v1/autorizacoes-cibio/MovimentacaoInterna/obterporfiltro?NumeroAutorizacaoCibio=${encodeURIComponent(texto)}`;

const urlObterAmiPorCodigo = (texto: string): string =>
	`api/v1/autorizacoes-cibio/MovimentacaoInterna/obterporfiltro?Codigo=${encodeURIComponent(texto)}`;

const candidatosCodigoAmi = (raw: string): string[] => {
	const t = raw.trim();
	if (!t) return [];
	const out: string[] = [];
	const push = (s: string) => {
		const x = s.trim();
		if (x && !out.includes(x)) out.push(x);
	};
	push(t);
	push(t.replace(/\//g, '.'));
	push(t.replace(/\./g, '/'));
	if (!/^AMI\s/i.test(t)) {
		push(`AMI ${t}`);
		push(`AMI ${t.replace(/\//g, '.')}`);
	}
	return out;
};

const tentarObterAmiPorIdNumerico = async (raw: string): Promise<number | null> => {
	const t = raw.trim();
	if (!/^\d+$/.test(t)) return null;
	const id = parseInt(t, 10);
	if (!Number.isFinite(id) || id <= 0) return null;
	try {
		const { data, status } = await api.get<{ id?: number }>(`api/v1/autorizacoes-cibio/MovimentacaoInterna/${id}`);
		if (status === 200 && typeof data?.id === 'number') return data.id;
	} catch {
	}
	return null;
};

const serializarDescricoesOutrasExtras = (itens: OutraSementeExtraLinha[]): string | null => {
	const objs: Record<string, unknown>[] = [];

	for (const item of itens) {
		if (!temAlgumDadoLinhaExtra(item)) continue;

		const outrasAmostrasRecebimento = converterNumero(item.outrasAmostrasRecebimento);
		const outrasPesoRecebimento = converterPesoKgParaNumeroTransporte(item.outrasPesoRecebimento);
		const outrasAmostrasPlantio = converterNumero(item.outrasAmostrasPlantio);
		const outrasPesoPlantio = converterPesoKgParaNumeroTransporte(item.outrasPesoPlantio);
		const outrasAmostrasSaldo = converterNumero(item.outrasAmostrasSaldo);
		const outrasPesoSaldo = converterPesoKgParaNumeroTransporte(item.outrasPesoSaldo);

		objs.push({
			descricao: item.texto.trim() || null,
			outrasDataRecebimento: parseDateValue(item.outrasDataRecebimento),
			outrasDataPlantio: parseDateValue(item.outrasDataPlantio),
			outrasDataSaldo: parseDateValue(item.outrasDataSaldo),
			outrasAmostrasRecebimento,
			outrasPesoRecebimento,
			outrasAmostrasPlantio,
			outrasPesoPlantio,
			outrasAmostrasSaldo,
			outrasPesoSaldo,
		});
	}

	if (objs.length === 0) return null;
	return JSON.stringify(objs);
};

const TransporteModal: React.FC<TransporteModalProps> = ({
	show,
	onHide,
	onSubmit,
	initialData,
	processoCTNBioReferencia,
}) => {
	const [abaTransporte, setAbaTransporte] = useState<1 | 2 | 3 | 4>(1);
	const [assinaturaRecebimento, setAssinaturaRecebimento] = useState<string | null>(null);
	const [assinaturaUsuario, setAssinaturaUsuario] = useState<string | null>(null);
	const [mostrarAssinatura, setMostrarAssinatura] = useState(false);
	const [loadingAssinatura, setLoadingAssinatura] = useState(false);
	const [opcoesLocais, setOpcoesLocais] = useState<LocalOption[]>([]);
	const [erroValidacao, setErroValidacao] = useState<string | null>(null);
	const [nomesEnvolvidosRecebimento, setNomesEnvolvidosRecebimento] = useState<string[]>([]);
	const [descricoesOutrasExtras, setDescricoesOutrasExtras] = useState<OutraSementeExtraLinha[]>([]);
	const [loadingAmiLink, setLoadingAmiLink] = useState(false);
	const [amisAdicionais, setAmisAdicionais] = useState<AmiAdicionalLinha[]>([]);
	const [loadingAmiLinkAdicionais, setLoadingAmiLinkAdicionais] = useState<Record<string, boolean>>({});

	const { control, handleSubmit, reset, getValues, setValue, watch } = useForm<TransporteFormValues>({
		defaultValues,
	});

	const valorAmiDigitado = watch('ami');

	const {
		ogmAmostrasRecebimento: watchOgmAmostrasRec,
		ogmPesoRecebimento: watchOgmPesoRec,
		ogmAmostrasPlantio: watchOgmAmostrasPlant,
		ogmPesoPlantio: watchOgmPesoPlant,
		outrasAmostrasRecebimento: watchOutrasAmostrasRec,
		outrasPesoRecebimento: watchOutrasPesoRec,
		outrasAmostrasPlantio: watchOutrasAmostrasPlant,
		outrasPesoPlantio: watchOutrasPesoPlant,
	} = watch([
		'ogmAmostrasRecebimento', 'ogmPesoRecebimento',
		'ogmAmostrasPlantio', 'ogmPesoPlantio',
		'outrasAmostrasRecebimento', 'outrasPesoRecebimento',
		'outrasAmostrasPlantio', 'outrasPesoPlantio',
	]);

	useEffect(() => {
		const calcSaldoAmostras = (
			recVal: any,
			plantVal: any,
			fieldPlantio: keyof TransporteFormValues,
			fieldSaldo: keyof TransporteFormValues,
		) => {
			const r = Math.max(Number(recVal) || 0, 0);
			const rawP = Math.max(Number(plantVal) || 0, 0);
			const p = Math.min(rawP, r);
			if (p !== rawP) setValue(fieldPlantio, String(p));
			setValue(fieldSaldo, String(r - p));
		};
		const calcSaldoPeso = (
			recVal: any,
			plantVal: any,
			fieldPlantio: keyof TransporteFormValues,
			fieldSaldo: keyof TransporteFormValues,
		) => {
			const r = Math.max(parseFloat(recVal) || 0, 0);
			const rawP = Math.max(parseFloat(plantVal) || 0, 0);
			const p = Math.min(rawP, r);
			if (Math.abs(p - rawP) > 0.001) setValue(fieldPlantio, p.toFixed(2));
			setValue(fieldSaldo, (r - p).toFixed(2));
		};
		calcSaldoAmostras(watchOgmAmostrasRec, watchOgmAmostrasPlant, 'ogmAmostrasPlantio', 'ogmAmostrasSaldo');
		calcSaldoPeso(watchOgmPesoRec, watchOgmPesoPlant, 'ogmPesoPlantio', 'ogmPesoSaldo');
		calcSaldoAmostras(watchOutrasAmostrasRec, watchOutrasAmostrasPlant, 'outrasAmostrasPlantio', 'outrasAmostrasSaldo');
		calcSaldoPeso(watchOutrasPesoRec, watchOutrasPesoPlant, 'outrasPesoPlantio', 'outrasPesoSaldo');
	}, [
		watchOgmAmostrasRec, watchOgmPesoRec, watchOgmAmostrasPlant, watchOgmPesoPlant,
		watchOutrasAmostrasRec, watchOutrasPesoRec, watchOutrasAmostrasPlant, watchOutrasPesoPlant,
	]);

	const handleAmiLinkClick = async (e: React.MouseEvent) => {
		e.preventDefault();
		const codigoBruto = getValues('ami')?.trim() ?? '';
		if (!codigoBruto) {
			alert('Informe o número da AMI.');
			return;
		}

		setLoadingAmiLink(true);
		try {
			const idDireto = await tentarObterAmiPorIdNumerico(codigoBruto);
			if (idDireto) {
				window.open(`${window.location.origin}/autorizacoes/visualizar/ami?id=${idDireto}`, '_blank');
				return;
			}

			let encontrados: AMISearchResult[] | null = null;
			const candidatos = candidatosCodigoAmi(codigoBruto);

			for (const texto of candidatos) {
				const { data } = await api.get<AMISearchResult[]>(urlObterAmiPorNumeroAutorizacaoCibio(texto));
				if (data && data.length > 0) {
					encontrados = data;
					break;
				}
			}

			if (!encontrados?.length) {
				for (const texto of candidatos) {
					const { data } = await api.get<AMISearchResult[]>(urlObterAmiPorCodigo(texto));
					if (data && data.length > 0) {
						encontrados = data;
						break;
					}
				}
			}

			if (encontrados && encontrados.length > 0) {
				const url = `${window.location.origin}/autorizacoes/visualizar/ami?id=${encontrados[0].id}`;
				window.open(url, '_blank');
			} else {
				alert(`AMI ${codigoBruto} não encontrada no sistema.`);
			}
		} catch (err) {
			console.error('Erro ao buscar AMI:', err);
			alert('Erro ao buscar a AMI. Verifique se o número está correto.');
		} finally {
			setLoadingAmiLink(false);
		}
	};

	const handleAmiLinkClickAdicional = async (e: React.MouseEvent, clientKey: string, codigoBruto: string) => {
		e.preventDefault();
		if (!codigoBruto.trim()) {
			alert('Informe o número da AMI.');
			return;
		}

		setLoadingAmiLinkAdicionais((prev) => ({ ...prev, [clientKey]: true }));
		try {
			const idDireto = await tentarObterAmiPorIdNumerico(codigoBruto.trim());
			if (idDireto) {
				window.open(`${window.location.origin}/autorizacoes/visualizar/ami?id=${idDireto}`, '_blank');
				return;
			}

			let encontrados: AMISearchResult[] | null = null;
			const candidatos = candidatosCodigoAmi(codigoBruto.trim());

			for (const texto of candidatos) {
				const { data } = await api.get<AMISearchResult[]>(urlObterAmiPorNumeroAutorizacaoCibio(texto));
				if (data && data.length > 0) {
					encontrados = data;
					break;
				}
			}

			if (!encontrados?.length) {
				for (const texto of candidatos) {
					const { data } = await api.get<AMISearchResult[]>(urlObterAmiPorCodigo(texto));
					if (data && data.length > 0) {
						encontrados = data;
						break;
					}
				}
			}

			if (encontrados && encontrados.length > 0) {
				window.open(`${window.location.origin}/autorizacoes/visualizar/ami?id=${encontrados[0].id}`, '_blank');
			} else {
				alert(`AMI ${codigoBruto.trim()} não encontrada no sistema.`);
			}
		} catch (err) {
			console.error('Erro ao buscar AMI:', err);
			alert('Erro ao buscar a AMI. Verifique se o número está correto.');
		} finally {
			setLoadingAmiLinkAdicionais((prev) => ({ ...prev, [clientKey]: false }));
		}
	};

	const carregarLocaisOrigem = async () => {
		try {
			const { data } = await api.get<CadernoLPMALocalLista[]>('api/v1/cadernoLPMA/cadernoLPMA/locais-lista');
			const lista = data ?? [];
			const opcoes: LocalOption[] = lista
				.map((item) => {
					const label = formatarLocalParaLabel(item);
					return label ? { label, value: label } : null;
				})
				.filter((o): o is LocalOption => Boolean(o));
			const unicos = Array.from(new Map(opcoes.map((o) => [o.value, o])).values());
			setOpcoesLocais((prev) => {
				const existentes = new Set(prev.map((p) => p.value));
				const novas = unicos.filter((o) => !existentes.has(o.value));
				return novas.length ? [...prev, ...novas] : prev.length ? prev : unicos;
			});
		} catch (e) {
			console.error('Erro ao carregar locais de origem:', e);
		}
	};

	useEffect(() => {
		if (!show) {
			reset(defaultValues);
			setAbaTransporte(1);
			setMostrarAssinatura(false);
			setAssinaturaRecebimento(null);
			setNomesEnvolvidosRecebimento([]);
			setDescricoesOutrasExtras([]);
			setAmisAdicionais([]);
			setLoadingAmiLinkAdicionais({});
			setErroValidacao(null);
			setLoadingAmiLink(false);
			return;
		}

		const valoresIniciais = converterValoresIniciais(initialData);
		reset(valoresIniciais);
		carregarAssinaturaUsuario();
		carregarLocaisOrigem();

		setDescricoesOutrasExtras(parseDescricoesOutrasExtrasJson(initialData?.outrasSementesAdicionaisJson));
		setAmisAdicionais(parseAmisAdicionaisJson(initialData?.amisAdicionaisJson));
		setNomesEnvolvidosRecebimento(parseNomesEnvolvidosJson(initialData?.nomesEnvolvidosAdicionaisRecebimentoJson));

		// Incluir valores iniciais nas opções para edição (quando valor já salvo não está na lista)
		const valorCampo = initialData?.localOrigemCampo?.trim();
		const valorCage = initialData?.localOrigemCage?.trim();
		if (valorCampo || valorCage) {
			setOpcoesLocais((prev) => {
				let next = [...prev];
				if (valorCampo && !next.some((o) => o.value === valorCampo)) {
					next.push({ label: valorCampo, value: valorCampo });
				}
				if (valorCage && !next.some((o) => o.value === valorCage)) {
					next.push({ label: valorCage, value: valorCage });
				}
				return next;
			});
		}

		// Carregar assinatura salva se existir
		if (initialData?.assinaturaResponsavelBase64) {
			setAssinaturaRecebimento(initialData.assinaturaResponsavelBase64);
		} else {
			setAssinaturaRecebimento(null);
		}
	}, [show, initialData, reset]);

	const carregarAssinaturaUsuario = async () => {
		try {
			const token = localStorage.getItem('token');
			if (token) {
				const decoded: any = JSON.parse(atob(token.split('.')[1]));
				const username = decoded.unique_name || '';
				
				const { data } = await api.get(`/api/v1/admin/usuarios/assinatura/${username}`);
				if (data && data.assinaturaBase64) {
					setAssinaturaUsuario(data.assinaturaBase64);
				}
			}
		} catch (error) {
			console.error('Erro ao carregar assinatura:', error);
		}
	};

	const handleInternalSubmit = handleSubmit(() => {
		// Usar getValues() no momento do submit garante todos os campos (incl. abas ocultas)
		const dados = getValues();

		const camposOutrasPrincipal = [
			dados.outrasDataRecebimento,
			dados.outrasAmostrasRecebimento,
			dados.outrasPesoRecebimento,
			dados.outrasDataPlantio,
			dados.outrasAmostrasPlantio,
			dados.outrasPesoPlantio,
			dados.outrasDataSaldo,
			dados.outrasAmostrasSaldo,
			dados.outrasPesoSaldo,
		];

		if (camposOutrasPrincipal.some(temValor) && !temValor(dados.outrasDescricao)) {
			setErroValidacao(
				'Preencha a descrição em "Outras (Especificar)" ao informar dados de recebimento, plantio ou saldo na primeira linha Outras.',
			);
			setAbaTransporte(2);
			return;
		}

		const camposDeUmaLinhaExtra = (ex: OutraSementeExtraLinha) => [
			ex.outrasDataRecebimento,
			ex.outrasDataPlantio,
			ex.outrasDataSaldo,
			ex.outrasAmostrasRecebimento,
			ex.outrasPesoRecebimento,
			ex.outrasAmostrasPlantio,
			ex.outrasPesoPlantio,
			ex.outrasAmostrasSaldo,
			ex.outrasPesoSaldo,
		];

		const linhaExtraSemDescricao = descricoesOutrasExtras.find(
			(ex) => camposDeUmaLinhaExtra(ex).some(temValor) && !temValor(ex.texto),
		);
		if (linhaExtraSemDescricao) {
			setErroValidacao(
				'Preencha a descrição em cada linha adicional de "Outras (Especificar)" em que houver dados de recebimento, plantio ou saldo.',
			);
			setAbaTransporte(2);
			return;
		}

		const msgPesoVirgula =
			'No Peso (kg) use apenas ponto como separador decimal (ex.: 2.31). Não use vírgula.';

		if (pesoCamposTabela.some((campo) => pesoStringTemVirgula((dados as any)[campo]))) {
			setErroValidacao(msgPesoVirgula);
			setAbaTransporte(2);
			return;
		}

		if (
			descricoesOutrasExtras.some((ex) =>
				chavesPesoLinhaExtra.some((k) => pesoStringTemVirgula(ex[k])),
			)
		) {
			setErroValidacao(msgPesoVirgula);
			setAbaTransporte(2);
			return;
		}

		const processado: CadernoLPMATransporte = {
			...dados,
			id: dados.id ?? 0,
			cadernoLPMAId: dados.cadernoLPMAId ?? 0,
			assinaturaResponsavelBase64: assinaturaRecebimento || null,
			nomesEnvolvidosAdicionaisRecebimentoJson: stringifyNomesEnvolvidos(nomesEnvolvidosRecebimento),
		};

		amostrasCampos.forEach((campo) => {
			(processado as any)[campo] = converterNumero((processado as any)[campo]);
		});

		pesoCamposTabela.forEach((campo) => {
			(processado as any)[campo] = converterPesoKgParaNumeroTransporte((processado as any)[campo]);
		});

		(processado as any).quantidadeSobrasSemeadoraKg = converterPesoKgParaNumero(
			dados.quantidadeSobrasSemeadoraKg,
		);

		const camposNumericosValidacao = [...numericCampos, 'quantidadeSobrasSemeadoraKg' as const];

		const numeroInvalido = camposNumericosValidacao.some((campo) => {
			const valor = (processado as any)[campo];
			return typeof valor === 'number' && valor < 0;
		});

		if (numeroInvalido) {
			setErroValidacao('Os campos numéricos de amostras e peso não podem ser negativos.');
			setAbaTransporte(2);
			return;
		}

		const extrasComNumeroNegativo = descricoesOutrasExtras.some((ex) => {
			const nums = [
				converterNumero(ex.outrasAmostrasRecebimento),
				converterPesoKgParaNumeroTransporte(ex.outrasPesoRecebimento),
				converterNumero(ex.outrasAmostrasPlantio),
				converterPesoKgParaNumeroTransporte(ex.outrasPesoPlantio),
				converterNumero(ex.outrasAmostrasSaldo),
				converterPesoKgParaNumeroTransporte(ex.outrasPesoSaldo),
			];
			return nums.some((n) => typeof n === 'number' && n < 0);
		});
		if (extrasComNumeroNegativo) {
			setErroValidacao('Os campos numéricos de amostras e peso não podem ser negativos.');
			setAbaTransporte(2);
			return;
		}

		const msgPlantioExcede = 'Os valores de Plantio não podem ser maiores que os de Recebimento.';

		const ogmAmostrasRecNum = converterNumero(dados.ogmAmostrasRecebimento) ?? 0;
		const ogmAmostrasPlantNum = converterNumero(dados.ogmAmostrasPlantio) ?? 0;
		const ogmPesoRecNum = converterPesoKgParaNumeroTransporte(dados.ogmPesoRecebimento) ?? 0;
		const ogmPesoPlantNum = converterPesoKgParaNumeroTransporte(dados.ogmPesoPlantio) ?? 0;
		const outrasAmostrasRecNum = converterNumero(dados.outrasAmostrasRecebimento) ?? 0;
		const outrasAmostrasPlantNum = converterNumero(dados.outrasAmostrasPlantio) ?? 0;
		const outrasPesoRecNum = converterPesoKgParaNumeroTransporte(dados.outrasPesoRecebimento) ?? 0;
		const outrasPesoPlantNum = converterPesoKgParaNumeroTransporte(dados.outrasPesoPlantio) ?? 0;

		if (
			(typeof ogmAmostrasPlantNum === 'number' && typeof ogmAmostrasRecNum === 'number' && ogmAmostrasPlantNum > ogmAmostrasRecNum) ||
			(typeof ogmPesoPlantNum === 'number' && typeof ogmPesoRecNum === 'number' && ogmPesoPlantNum > ogmPesoRecNum) ||
			(typeof outrasAmostrasPlantNum === 'number' && typeof outrasAmostrasRecNum === 'number' && outrasAmostrasPlantNum > outrasAmostrasRecNum) ||
			(typeof outrasPesoPlantNum === 'number' && typeof outrasPesoRecNum === 'number' && outrasPesoPlantNum > outrasPesoRecNum)
		) {
			setErroValidacao(msgPlantioExcede);
			setAbaTransporte(2);
			return;
		}

		const extraComPlantioExcedente = descricoesOutrasExtras.some((ex) => {
			const recA = converterNumero(ex.outrasAmostrasRecebimento) ?? 0;
			const plantA = converterNumero(ex.outrasAmostrasPlantio) ?? 0;
			const recP = converterPesoKgParaNumeroTransporte(ex.outrasPesoRecebimento) ?? 0;
			const plantP = converterPesoKgParaNumeroTransporte(ex.outrasPesoPlantio) ?? 0;
			return (
				(typeof plantA === 'number' && typeof recA === 'number' && plantA > recA) ||
				(typeof plantP === 'number' && typeof recP === 'number' && plantP > recP)
			);
		});
		if (extraComPlantioExcedente) {
			setErroValidacao(msgPlantioExcede);
			setAbaTransporte(2);
			return;
		}

		camposData.forEach((campo) => {
			(processado as any)[campo] = parseDateValue((processado as any)[campo]);
		});

		(processado as any).outrasSementesAdicionaisJson = serializarDescricoesOutrasExtras(descricoesOutrasExtras);
		(processado as any).amisAdicionaisJson = serializarAmisAdicionais(amisAdicionais);

		setErroValidacao(null);
		onSubmit(processado);
	});

	const renderDatePicker = (name: keyof TransporteFormValues) => (
		<Controller
			control={control}
			name={name as any}
			render={({ onChange, value }) => (
				<DatePicker
					selected={parseDateValue(value)}
					onChange={onChange}
					className="input-custom-to-date"
					locale="pt-BR"
					dateFormat="dd/MM/yyyy"
					placeholderText="Selecionar a data"
				/>
			)}
		/>
	);

	const renderDatePickerCompact = (name: keyof TransporteFormValues) => (
		<Controller
			control={control}
			name={name as any}
			render={({ onChange, value }) => (
				<DatePicker
					selected={parseDateValue(value)}
					onChange={onChange}
					className="input-custom-to-date input-custom-to-date-compact"
					locale="pt-BR"
					dateFormat="dd/MM/yyyy"
					placeholderText="Selecionar a data"
				/>
			)}
		/>
	);

	const estiloInputTabelaCompacta: React.CSSProperties = { height: '30px', fontSize: '12px', padding: '4px 6px' };

	const renderDatePickerCompactControlado = (
		value: Date | null,
		onChange: (d: Date | null) => void,
	) => (
		<DatePicker
			selected={parseDateValue(value)}
			onChange={(d) => {
				const v = Array.isArray(d) ? d[0] : d;
				onChange(v ?? null);
			}}
			className="input-custom-to-date input-custom-to-date-compact"
			locale="pt-BR"
			dateFormat="dd/MM/yyyy"
			placeholderText="Selecionar a data"
		/>
	);

	const renderCheckbox = (
		name: keyof TransporteFormValues,
		label: string,
		props?: Omit<React.ComponentProps<typeof Form.Check>, 'type' | 'label' | 'checked' | 'onChange'>,
	) => (
		<Controller
			control={control}
			name={name as any}
			render={({ value, onChange }) => (
				<Form.Check
					type="checkbox"
					label={label}
					checked={Boolean(value)}
					onChange={(event) => onChange(event.target.checked)}
					{...props}
				/>
			)}
		/>
	);

	const calcularSaldoLinha = (row: OutraSementeExtraLinha): OutraSementeExtraLinha => {
		const recAmostras = Math.max(Number(row.outrasAmostrasRecebimento) || 0, 0);
		const rawPlantAmostras = Math.max(Number(row.outrasAmostrasPlantio) || 0, 0);
		const plantAmostras = Math.min(rawPlantAmostras, recAmostras);
		const recPeso = Math.max(parseFloat(row.outrasPesoRecebimento) || 0, 0);
		const rawPlantPeso = Math.max(parseFloat(row.outrasPesoPlantio) || 0, 0);
		const plantPeso = Math.min(rawPlantPeso, recPeso);
		return {
			...row,
			outrasAmostrasPlantio: String(plantAmostras),
			outrasPesoPlantio: plantPeso.toFixed(2),
			outrasAmostrasSaldo: String(recAmostras - plantAmostras),
			outrasPesoSaldo: (recPeso - plantPeso).toFixed(2),
		};
	};

	const renderInput = (
		name: keyof TransporteFormValues,
		props?: React.ComponentProps<typeof Form.Control>,
	) => (
		<Controller
			control={control}
			name={name as any}
			render={({ value, onChange }) => (
				<Form.Control
					{...props}
					value={value ?? ''}
					onChange={(event) => onChange(event.target.value)}
				/>
			)}
		/>
	);

	const renderInputPesoKgCampo = (
		name: keyof TransporteFormValues,
		props?: Omit<React.ComponentProps<typeof Form.Control>, 'type' | 'value' | 'onChange' | 'onBlur'>,
		isAllowed?: (values: any) => boolean,
	) => (
		<Controller
			control={control}
			name={name as any}
			render={({ value, onChange }) => {
				const valorStr = value === null || value === undefined ? '' : String(value);
				return (
					<NumberFormat
						customInput={Form.Control}
						{...props}
						type="text"
						inputMode="decimal"
						autoComplete="off"
						thousandSeparator={false}
						decimalSeparator="."
						decimalScale={2}
						fixedDecimalScale
						allowNegative={false}
						allowEmptyFormatting={false}
						value={valorStr}
						isNumericString={valorStr !== ''}
						isAllowed={isAllowed}
						onValueChange={(vals) => {
							if (vals.value === '') {
								onChange('');
							} else {
								onChange(vals.value);
							}
						}}
					/>
				);
			}}
		/>
	);

	const renderPesoKgLinhaExtra = (
		valor: string,
		onChange: (v: string) => void,
		isAllowedOrStyle?: ((values: any) => boolean) | React.CSSProperties,
		style?: React.CSSProperties,
	) => {
		const valorStr = valor ?? '';
		const isAllowed = typeof isAllowedOrStyle === 'function' ? isAllowedOrStyle : undefined;
		const resolvedStyle = typeof isAllowedOrStyle === 'function' ? style : isAllowedOrStyle;
		return (
			<NumberFormat
				customInput={Form.Control}
				type="text"
				inputMode="decimal"
				autoComplete="off"
				placeholder="0.00"
				thousandSeparator={false}
				decimalSeparator="."
				decimalScale={2}
				fixedDecimalScale
				allowNegative={false}
				allowEmptyFormatting={false}
				value={valorStr}
				isNumericString={valorStr !== ''}
				isAllowed={isAllowed}
				onValueChange={(vals) => {
					if (vals.value === '') {
						onChange('');
					} else {
						onChange(vals.value);
					}
				}}
				style={resolvedStyle ?? estiloInputTabelaCompacta}
			/>
		);
	};

	const renderInputPesoKg = (
		props?: Omit<React.ComponentProps<typeof Form.Control>, 'type' | 'value' | 'onChange' | 'onBlur'>,
	) => (
		<Controller
			control={control}
			name="quantidadeSobrasSemeadoraKg"
			render={({ value, onChange }) => {
				const v = value ?? '';
				const valorStr = v === null || v === undefined ? '' : String(v);
				return (
					<NumberFormat
						customInput={Form.Control}
						{...props}
						type="text"
						inputMode="decimal"
						autoComplete="off"
						thousandSeparator={false}
						decimalSeparator="."
						decimalScale={2}
						fixedDecimalScale
						allowNegative={false}
						allowEmptyFormatting={false}
						value={valorStr}
						isNumericString={valorStr !== ''}
						onValueChange={(vals) => {
							if (vals.value === '') {
								onChange('');
							} else {
								onChange(vals.value);
							}
						}}
					/>
				);
			}}
		/>
	);

	const renderSelectLocalOrigem = (name: 'localOrigemCampo' | 'localOrigemCage') => (
		<Controller
			control={control}
			name={name}
			render={({ value, onChange }) => {
				const valor = (value ?? '') as string;
				const opcao = valor
					? opcoesLocais.find((o) => o.value === valor) ?? { label: valor, value: valor }
					: null;
				return (
					<Select<LocalOption>
						placeholder="Selecione o local (Estação, Cidade/UF)"
						options={opcoesLocais}
						value={opcao}
						onChange={(opt) => onChange(opt?.value ?? '')}
						isClearable
						classNamePrefix="react-select"
						noOptionsMessage={() => 'Nenhum local encontrado'}
					/>
				);
			}}
		/>
	);

	const abasTransporte = [
		{ valor: 1 as 1 | 2 | 3 | 4, label: 'Origem' },
		{ valor: 2 as 1 | 2 | 3 | 4, label: 'Recebimento' },
		{ valor: 3 as 1 | 2 | 3 | 4, label: 'Destino/Saldo' },
		{ valor: 4 as 1 | 2 | 3 | 4, label: 'Checklist' },
	];

	return (
		<Modal
			show={show}
			onHide={onHide}
			title={STRINGS_CADERNOLPMA.TRANSPORTE_MODAL_TITLE}
			bodyClassName="caderno-lpma-modal-body"
		>
			<Form onSubmit={handleInternalSubmit} autoComplete="off">
				{erroValidacao && (
					<Alert variant="danger" className="mb-3">
						{erroValidacao}
					</Alert>
				)}
				{/* Navegação de Abas */}
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<div className="button-position-caderno" style={{ justifyContent: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
							{abasTransporte.map((aba) => (
								<Button
									key={aba.valor}
									title={aba.label}
									contained={abaTransporte === aba.valor}
									className={abaTransporte === aba.valor ? 'active' : ''}
									type="button"
									onClick={() => setAbaTransporte(aba.valor)}
								/>
							))}
						</div>
					</Form.Group>
				</Form.Row>

				<hr className="my-3" />

				{/* ABA 1 - ORIGEM E QUANTIDADE */}
				<div style={{ display: abaTransporte === 1 ? 'block' : 'none' }}>
						<Form.Row className="row-custom">
							<Form.Group as={Col} md={12}>
								{renderCheckbox('multiplicadaCampo', 'Multiplicada em campo/LPMA')}
							</Form.Group>
						</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={6}>
						<Form.Label>Processo(s) CTNBio</Form.Label>
						{renderInput('processoCIBIO', {
							placeholder: 'Insira o(s) número(s) de processo(s) CIBIO',
						})}
					</Form.Group>
					<Form.Group as={Col} md={6}>
						<Form.Label>Local de Origem</Form.Label>
						{renderSelectLocalOrigem('localOrigemCampo')}
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						{renderCheckbox('multiplicadaCage', 'Multiplicada em Casa de Vegetação/CAGE')}
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={6}>
						<Form.Label>AAC</Form.Label>
						{renderInput('aac', {
							placeholder: 'Insira a Autorização de Atividade em Contenção (AAC)',
						})}
					</Form.Group>
					<Form.Group as={Col} md={6}>
						<Form.Label>Local de Origem</Form.Label>
						{renderSelectLocalOrigem('localOrigemCage')}
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						{renderCheckbox('importada', 'Importada')}
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={6}>
						<Form.Label>Processo MAPA</Form.Label>
						{renderInput('processoMAPA', {
							placeholder: 'Insira o número de processo MAPA',
						})}
					</Form.Group>
					<Form.Group as={Col} md={6}>
						<Form.Label>Processo de Importação (IP)</Form.Label>
						{renderInput('processoImportacaoIP', {
							placeholder: 'Insira o número do processo de importação (IP)',
						})}
					</Form.Group>
				</Form.Row>
				</div>

				{/* ABA 2 - RECEBIMENTO */}
				<div style={{ display: abaTransporte === 2 ? 'block' : 'none' }}>
					<Form.Row className="row-custom">
						<Form.Group as={Col} md={12}>
							<Form.Label>
								<h2>Recebimento de sementes e cadeia de custódia</h2>
							</Form.Label>
							<Form.Text className="d-block">
								* Em caso de mais de uma AMI, somar e registrar abaixo a quantidade informada em todas as AMIs.
							</Form.Text>
							<Form.Text className="d-block mb-3">
								** Entende-se como data de recebimento a data que a semente chegou na Unidade Operativa para armazenamento.
							</Form.Text>
						</Form.Group>
					</Form.Row>

					<div style={{ border: '1px solid #9b9b9b', marginBottom: '16px' }}>
						<table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
							<thead>
								<tr style={{ backgroundColor: '#00884A', color: '#fff' }}>
									<th style={{ border: '1px solid #9b9b9b', padding: '6px 4px', width: '16%', fontSize: '12px' }}>Tipo Semente</th>
									<th style={{ border: '1px solid #9b9b9b', padding: '6px 4px', fontSize: '12px' }} colSpan={3}>Recebimento</th>
									<th style={{ border: '1px solid #9b9b9b', padding: '6px 4px', fontSize: '12px' }} colSpan={3}>Plantio</th>
									<th style={{ border: '1px solid #9b9b9b', padding: '6px 4px', fontSize: '12px' }} colSpan={3}>Saldo sementes não plantadas</th>
								</tr>
								<tr style={{ backgroundColor: '#f7f7f7' }}>
									<th style={{ border: '1px solid #9b9b9b', padding: '4px' }} />
									<th style={{ border: '1px solid #9b9b9b', padding: '4px', fontSize: '12px', lineHeight: 1.15 }}>Data</th>
									<th style={{ border: '1px solid #9b9b9b', padding: '4px', fontSize: '12px', lineHeight: 1.15 }}>nº<br />Amostras</th>
									<th style={{ border: '1px solid #9b9b9b', padding: '4px', fontSize: '12px', lineHeight: 1.15 }}>Peso (kg)</th>
									<th style={{ border: '1px solid #9b9b9b', padding: '4px', fontSize: '12px', lineHeight: 1.15 }}>Data</th>
									<th style={{ border: '1px solid #9b9b9b', padding: '4px', fontSize: '12px', lineHeight: 1.15 }}>nº<br />Amostras</th>
									<th style={{ border: '1px solid #9b9b9b', padding: '4px', fontSize: '12px', lineHeight: 1.15 }}>Peso (kg)</th>
									<th style={{ border: '1px solid #9b9b9b', padding: '4px', fontSize: '12px', lineHeight: 1.15 }}>Data</th>
									<th style={{ border: '1px solid #9b9b9b', padding: '4px', fontSize: '12px', lineHeight: 1.15 }}>nº<br />Amostras</th>
									<th style={{ border: '1px solid #9b9b9b', padding: '4px', fontSize: '12px', lineHeight: 1.15 }}>Peso (kg)</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td style={{ border: '1px solid #9b9b9b', padding: '4px 6px', fontWeight: 600, fontSize: '12px' }}>OGM</td>
									<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>{renderDatePickerCompact('dataRecebimento')}</td>
									<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>{renderInput('ogmAmostrasRecebimento', { type: 'number', min: 0, placeholder: '0', style: { height: '30px', fontSize: '12px', padding: '4px 6px' } })}</td>
									<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>
										{renderInputPesoKgCampo('ogmPesoRecebimento', {
											min: 0,
											placeholder: '0.00',
											style: { height: '30px', fontSize: '12px', padding: '4px 6px' },
										})}
									</td>
									<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>{renderDatePickerCompact('dataPlantio')}</td>
								<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>{renderInput('ogmAmostrasPlantio', { type: 'number', min: 0, max: Number(watchOgmAmostrasRec) || undefined, placeholder: '0', style: { height: '30px', fontSize: '12px', padding: '4px 6px' } })}</td>
								<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>
									{renderInputPesoKgCampo('ogmPesoPlantio', {
										min: 0,
										placeholder: '0.00',
										style: { height: '30px', fontSize: '12px', padding: '4px 6px' },
									}, (vals) => (parseFloat(vals.value) || 0) <= (parseFloat(watchOgmPesoRec) || 0))}
									</td>
									<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>{renderDatePickerCompact('dataSaldo')}</td>
								<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>{renderInput('ogmAmostrasSaldo', { type: 'number', readOnly: true, placeholder: '0', style: { height: '30px', fontSize: '12px', padding: '4px 6px', backgroundColor: '#f5f5f5', cursor: 'default' } })}</td>
								<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>
									{renderInputPesoKgCampo('ogmPesoSaldo', {
										readOnly: true,
										placeholder: '0.00',
										style: { height: '30px', fontSize: '12px', padding: '4px 6px', backgroundColor: '#f5f5f5', cursor: 'default' },
									})}
								</td>
								</tr>
								<tr>
									<td style={{ border: '1px solid #9b9b9b', padding: '4px 6px' }}>
										<div style={{ fontWeight: 600, fontSize: '12px', marginBottom: 4 }}>
											Outras (Especificar)
										</div>
										<div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
											<button
												type="button"
												className="btn btn-link p-0 d-inline-flex align-items-center justify-content-center flex-shrink-0"
												style={{
													width: 18,
													height: 18,
													minWidth: 18,
													fontSize: '14px',
													lineHeight: 1,
													color: '#00884A',
													textDecoration: 'none',
													border: '1px solid #00884A',
													borderRadius: 3,
												}}
												title="Adicionar linha Outras"
												aria-label="Adicionar linha Outras"
												onClick={() => setDescricoesOutrasExtras((p) => [...p, novaDescricaoOutraExtra()])}
											>
												+
											</button>
											<div style={{ flex: 1, minWidth: 0 }}>
												{renderInput('outrasDescricao', {
													placeholder: 'Descrição',
													style: estiloInputTabelaCompacta,
												})}
											</div>
										</div>
									</td>
									<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>{renderDatePickerCompact('outrasDataRecebimento' as any)}</td>
									<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>{renderInput('outrasAmostrasRecebimento', { type: 'number', min: 0, placeholder: '0', style: estiloInputTabelaCompacta })}</td>
									<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>
										{renderInputPesoKgCampo('outrasPesoRecebimento', {
											min: 0,
											placeholder: '0.00',
											style: estiloInputTabelaCompacta,
										})}
									</td>
									<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>{renderDatePickerCompact('outrasDataPlantio' as any)}</td>
								<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>{renderInput('outrasAmostrasPlantio', { type: 'number', min: 0, max: Number(watchOutrasAmostrasRec) || undefined, placeholder: '0', style: estiloInputTabelaCompacta })}</td>
								<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>
									{renderInputPesoKgCampo('outrasPesoPlantio', {
										min: 0,
										placeholder: '0.00',
										style: estiloInputTabelaCompacta,
									}, (vals) => (parseFloat(vals.value) || 0) <= (parseFloat(watchOutrasPesoRec) || 0))}
									</td>
									<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>{renderDatePickerCompact('outrasDataSaldo' as any)}</td>
								<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>{renderInput('outrasAmostrasSaldo', { type: 'number', readOnly: true, placeholder: '0', style: { ...estiloInputTabelaCompacta, backgroundColor: '#f5f5f5', cursor: 'default' } })}</td>
								<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>
									{renderInputPesoKgCampo('outrasPesoSaldo', {
										readOnly: true,
										placeholder: '0.00',
										style: { ...estiloInputTabelaCompacta, backgroundColor: '#f5f5f5', cursor: 'default' },
									})}
								</td>
								</tr>
								{descricoesOutrasExtras.map((item, idx) => (
									<tr key={item.clientKey}>
										<td style={{ border: '1px solid #9b9b9b', padding: '4px 6px' }}>
											<div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
												<button
													type="button"
													className="btn btn-link p-0 d-inline-flex align-items-center justify-content-center flex-shrink-0"
													style={{
														width: 18,
														height: 18,
														minWidth: 18,
														color: '#00884A',
														textDecoration: 'none',
														border: '1px solid #00884A',
														borderRadius: 3,
													}}
													title="Remover linha"
													aria-label="Remover linha"
													onClick={() => setDescricoesOutrasExtras((p) => p.filter((_, i) => i !== idx))}
												>
													<FaTrashAlt aria-hidden size={11} color="#00884A" />
												</button>
												<div style={{ flex: 1, minWidth: 0 }}>
													<Form.Control
														value={item.texto}
														onChange={(e) =>
															setDescricoesOutrasExtras((prev) =>
																prev.map((row, i) => (i === idx ? { ...row, texto: e.target.value } : row)),
															)
														}
														placeholder="Descrição"
														style={estiloInputTabelaCompacta}
													/>
												</div>
											</div>
										</td>
										<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>
											{renderDatePickerCompactControlado(item.outrasDataRecebimento, (d) =>
												setDescricoesOutrasExtras((prev) =>
													prev.map((row, i) => (i === idx ? { ...row, outrasDataRecebimento: d } : row)),
												),
											)}
										</td>
									<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>
										<Form.Control
											type="number"
											min={0}
											placeholder="0"
											style={estiloInputTabelaCompacta}
											value={item.outrasAmostrasRecebimento}
											onChange={(e) =>
												setDescricoesOutrasExtras((prev) =>
													prev.map((row, i) =>
														i === idx ? calcularSaldoLinha({ ...row, outrasAmostrasRecebimento: e.target.value }) : row,
													),
												)
											}
										/>
									</td>
									<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>
										{renderPesoKgLinhaExtra(item.outrasPesoRecebimento, (v) =>
											setDescricoesOutrasExtras((prev) =>
												prev.map((row, i) =>
													i === idx ? calcularSaldoLinha({ ...row, outrasPesoRecebimento: v }) : row,
												),
											),
										)}
									</td>
									<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>
										{renderDatePickerCompactControlado(item.outrasDataPlantio, (d) =>
											setDescricoesOutrasExtras((prev) =>
												prev.map((row, i) => (i === idx ? { ...row, outrasDataPlantio: d } : row)),
											),
										)}
									</td>
									<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>
										<Form.Control
											type="number"
											min={0}
											max={Number(item.outrasAmostrasRecebimento) || undefined}
											placeholder="0"
											style={estiloInputTabelaCompacta}
											value={item.outrasAmostrasPlantio}
											onChange={(e) =>
												setDescricoesOutrasExtras((prev) =>
													prev.map((row, i) =>
														i === idx ? calcularSaldoLinha({ ...row, outrasAmostrasPlantio: e.target.value }) : row,
													),
												)
											}
										/>
									</td>
									<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>
										{renderPesoKgLinhaExtra(item.outrasPesoPlantio, (v) =>
											setDescricoesOutrasExtras((prev) =>
												prev.map((row, i) =>
													i === idx ? calcularSaldoLinha({ ...row, outrasPesoPlantio: v }) : row,
												),
											),
										(vals: any) => (parseFloat(vals?.value) || 0) <= (parseFloat(item.outrasPesoRecebimento) || 0))}
									</td>
										<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>
											{renderDatePickerCompactControlado(item.outrasDataSaldo, (d) =>
												setDescricoesOutrasExtras((prev) =>
													prev.map((row, i) => (i === idx ? { ...row, outrasDataSaldo: d } : row)),
												),
											)}
										</td>
									<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>
										<Form.Control
											type="number"
											readOnly
											placeholder="0"
											style={{ ...estiloInputTabelaCompacta, backgroundColor: '#f5f5f5', cursor: 'default' }}
											value={item.outrasAmostrasSaldo}
											onChange={() => {}}
										/>
									</td>
									<td style={{ border: '1px solid #9b9b9b', padding: '3px' }}>
										<Form.Control
											readOnly
											placeholder="0.00"
											style={{ ...estiloInputTabelaCompacta, backgroundColor: '#f5f5f5', cursor: 'default' }}
											value={item.outrasPesoSaldo}
											onChange={() => {}}
										/>
									</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

			{/* Bloco de N.º da AMI (múltiplos) */}
			<Form.Row className="row-custom">
				<Form.Group as={Col} md={12}>
					<Form.Label className="small">N.&ordm; da AMI</Form.Label>

					<div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
						<div style={{ flex: 1 }}>
							{renderInput('ami', {
								placeholder: 'Informe o número da AMI',
							})}
							{valorAmiDigitado?.trim() ? (
								<div style={{ marginTop: '4px' }}>
									<a
										href="#"
										onClick={handleAmiLinkClick}
										style={{
											color: '#0066cc',
											textDecoration: 'none',
											fontSize: '13px',
											display: 'inline-flex',
											alignItems: 'center',
											gap: '5px',
											cursor: loadingAmiLink ? 'wait' : 'pointer',
											pointerEvents: loadingAmiLink ? 'none' : 'auto',
											opacity: loadingAmiLink ? 0.6 : 1,
										}}
										title="Abrir AMI em nova guia"
									>
										<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
											<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
											<polyline points="15 3 21 3 21 9" />
											<line x1="10" y1="14" x2="21" y2="3" />
										</svg>
										{loadingAmiLink ? 'Buscando AMI...' : `Visualizar AMI ${valorAmiDigitado.trim()}`}
									</a>
								</div>
							) : null}
						</div>
						<button
							type="button"
							className="btn btn-link p-0 d-inline-flex align-items-center justify-content-center flex-shrink-0"
							style={{
								width: 28,
								height: 34,
								minWidth: 28,
								fontSize: '18px',
								lineHeight: 1,
								color: '#00884A',
								textDecoration: 'none',
								border: '1px solid #00884A',
								borderRadius: 4,
							}}
							title="Adicionar AMI"
							aria-label="Adicionar AMI"
							onClick={() => setAmisAdicionais((prev) => [...prev, novaAmiAdicional()])}
						>
							+
						</button>
					</div>

					{amisAdicionais.map((item) => {
						const amiTrimItem = item.ami.trim();
						const loadingItem = loadingAmiLinkAdicionais[item.clientKey] ?? false;
						return (
							<div key={item.clientKey} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
								<div style={{ flex: 1 }}>
									<Form.Control
										value={item.ami}
										onChange={(e) =>
											setAmisAdicionais((prev) =>
												prev.map((row) =>
													row.clientKey === item.clientKey ? { ...row, ami: e.target.value } : row,
												),
											)
										}
										placeholder="Informe o número da AMI"
									/>
									{amiTrimItem ? (
										<div style={{ marginTop: '4px' }}>
											<a
												href="#"
												onClick={(e) => handleAmiLinkClickAdicional(e, item.clientKey, amiTrimItem)}
												style={{
													color: '#0066cc',
													textDecoration: 'none',
													fontSize: '13px',
													display: 'inline-flex',
													alignItems: 'center',
													gap: '5px',
													cursor: loadingItem ? 'wait' : 'pointer',
													pointerEvents: loadingItem ? 'none' : 'auto',
													opacity: loadingItem ? 0.6 : 1,
												}}
												title="Abrir AMI em nova guia"
											>
												<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
													<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
													<polyline points="15 3 21 3 21 9" />
													<line x1="10" y1="14" x2="21" y2="3" />
												</svg>
												{loadingItem ? 'Buscando AMI...' : `Visualizar AMI ${amiTrimItem}`}
											</a>
										</div>
									) : null}
								</div>
								<button
									type="button"
									className="btn btn-link p-0 d-inline-flex align-items-center justify-content-center flex-shrink-0"
									style={{
										width: 28,
										height: 34,
										minWidth: 28,
										color: '#00884A',
										textDecoration: 'none',
										border: '1px solid #00884A',
										borderRadius: 4,
									}}
									title="Remover AMI"
									aria-label="Remover AMI"
									onClick={() =>
										setAmisAdicionais((prev) =>
											prev.filter((row) => row.clientKey !== item.clientKey),
										)
									}
								>
									<FaTrashAlt aria-hidden size={12} color="#00884A" />
								</button>
							</div>
						);
					})}
				</Form.Group>
			</Form.Row>

			<Form.Row className="row-custom">
				<Form.Group as={Col} md={12}>
					<Form.Label className="small">N.&ordm; da Carta de Transferência</Form.Label>
					{renderInput('cartaTransferencia', {
						placeholder: 'Informe o número da carta de transferência',
					})}
				</Form.Group>
			</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							Data do recebimento/conferência das sementes
						</Form.Label>
						{renderDatePicker('dataResponsavel')}
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Nome do responsável</Form.Label>
						{renderInput('responsavel', {
							placeholder: 'Insira o nome do responsável',
						})}
					</Form.Group>
				</Form.Row>

				<hr className="my-4" />

				<AssinaturaComNomesAdicionais
					label="Assinatura do Responsável"
					assinaturaUsuario={assinaturaUsuario}
					assinaturaBase64={assinaturaRecebimento}
					onAssinaturaBase64Change={setAssinaturaRecebimento}
					nomesAdicionais={nomesEnvolvidosRecebimento}
					onNomesAdicionaisChange={setNomesEnvolvidosRecebimento}
					mostrarAssinatura={mostrarAssinatura}
					onMostrarAssinaturaChange={setMostrarAssinatura}
				/>
				</div>

				{/* ABA 3 - DESTINO/SALDO */}
				<div style={{ display: abaTransporte === 3 ? 'block' : 'none' }}>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h2>Destino em caso de saldo/sobras de sementes do plantio</h2>
						</Form.Label>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						{renderCheckbox(
							'destruicaoDescarte',
							'Destruição e descarte (preencher formulário de destruição e descarte neste caderno)',
						)}
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						{renderCheckbox(
							'armazenamento',
							'Armazenamento na mesma Estação (inserir lista de amostras armazenadas na ABA LISTAS)',
						)}
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						{renderCheckbox(
							'devolucao',
							'Devolução ou envio para outra Estação (anexar autorização de transporte/movimentação interna da CIBio na ABA AMI)',
						)}
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Observações e informações adicionais</Form.Label>
						{renderInput('observacao', {
							as: 'textarea',
							rows: 2,
							placeholder: 'Insira as observações',
						})}
					</Form.Group>
				</Form.Row>

				<hr className="my-4" />

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h2>Sobras de sementes oriundas da limpeza da semeadora</h2>
						</Form.Label>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom align-items-center">
					<Form.Group as={Col} md={12} className="d-flex align-items-center">
						<div className="d-flex align-items-center" style={{ gap: '10px', flexWrap: 'wrap' }}>
							{renderCheckbox(
								'sobrasSementesSemeadora',
								'Somente sobras de sementes oriundas da limpeza da semeadora/quantidade estimada:',
							)}
							<div className="d-flex align-items-center" style={{ gap: '5px', minWidth: '150px' }}>
								{renderInputPesoKg({
									placeholder: '0.00',
									style: { maxWidth: '120px' },
								})}
								<span>Kg</span>
							</div>
							<span className="text-muted">(preencher formulário de destruição e descarte neste caderno)</span>
						</div>
					</Form.Group>
				</Form.Row>
				</div>

				{/* ABA 4 - CHECKLIST */}
				<div style={{ display: abaTransporte === 4 ? 'block' : 'none' }}>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h2>Checklist - Recebimento de Sementes</h2>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						{renderCheckbox(
							'origemCoincideLPMA',
							'a. A origem das sementes coincide com o previsto e aprovado na LPMA?',
						)}
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						{renderInput('observacaoOrigemCoincideLPMA', {
							as: 'textarea',
							rows: 2,
							placeholder: 'Comentários',
						})}
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						{renderCheckbox(
							'transporteAutorizado',
							'b. O transporte das sementes foi autorizado pela CIBio, e autorização se encontra disponível?',
						)}
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						{renderInput('observacaoTransporteAutorizado', {
							as: 'textarea',
							rows: 2,
							placeholder: 'Comentários',
						})}
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						{renderCheckbox(
							'embalagens',
							'c. As embalagens com as sementes estão devidamente identificadas?',
						)}
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						{renderInput('observacaoEmbalagens', {
							as: 'textarea',
							rows: 2,
							placeholder: 'Comentários',
						})}
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						{renderCheckbox(
							'duplaContencao',
							'd. As sementes foram transportadas ou movimentadas seguindo o princípio da dupla contenção e as embalagens estavam fisicamente íntegras?',
						)}
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						{renderInput('observacaoDuplaContencao', {
							as: 'textarea',
							rows: 2,
							placeholder: 'Comentários',
						})}
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						{renderCheckbox(
							'concordanciaAMI',
							'e. A quantidade de semente transportada e recebida (peso/número de amostras) condiz com o descrito na Autorização de Movimentação Interna (AMI) e/ou carta de transferência?',
						)}
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						{renderInput('observacaoConcordanciaAMI', {
							as: 'textarea',
							rows: 2,
							placeholder: 'Comentários',
						})}
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						{renderCheckbox(
							'localPossuiCQB',
							'f. O local de armazenamento possui CQB? O local está identificado e possui acesso controlado?',
						)}
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						{renderInput('observacaoLocalPossuiCQB', {
							as: 'textarea',
							rows: 2,
							placeholder: 'Comentários',
						})}
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						{renderCheckbox(
							'quantidadeRegistrada',
							'g. A quantidade de sementes recebida foi registrada como entrada na cadeia de custódia deste Caderno de Conformidade de Campo?',
						)}
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Se pertinente comentar:</Form.Label>
						{renderInput('observacaoQuantidadeRegistrada', {
							as: 'textarea',
							rows: 2,
							placeholder: 'Comentários',
						})}
					</Form.Group>
				</Form.Row>
				</div>

				<div className="button-position">
					<Button title="Cancelar" contained={false} type="button" onClick={onHide} />
					<Button title="Inserir" contained type="submit" />
				</div>
			</Form>
		</Modal>
	);
};

export default TransporteModal;

